import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarRange, ClipboardEdit, FileText, Inbox, Send, ShieldCheck, Tags, Users, type LucideIcon } from "lucide-react";

import { academicSetupService } from "../services/academicSetupService";
import { examinationService } from "../services/examinationService";
import { getAll } from "../services/pagination";
import { usePermissions } from "../hooks/usePermissions";
import { useAuthStore } from "../stores/authStore";
import { MarkSheetPanel } from "../components/examinations/MarkSheetPanel";
import { PapersPanel } from "../components/examinations/PapersPanel";
import { CandidatesPanel, PeriodsPanel, TypesPanel } from "../components/examinations/SetupPanels";
import { nextAction } from "../components/examinations/examLogic";
import type { ClassOption, SubjectOption } from "../components/examinations/ExamForms";

type Tab = "papers" | "marks" | "candidates" | "periods" | "types";

/** Examinations: set papers, mark them, and move results through moderation and approval to publication. */
export default function Examinations() {
  const { hasCapability } = usePermissions();
  const me = useAuthStore((state) => state.user?.id);
  const isManager = hasCapability("access.examination.manage_exams");
  const can = {
    canModerate: hasCapability("access.examination.moderate_results"),
    canApprove: hasCapability("access.examination.approve_results"),
    canPublish: hasCapability("access.examination.publish_results"),
  };
  const tabs: { id: Tab; label: string; icon: LucideIcon }[] = [
    { id: "papers", label: "Papers", icon: FileText },
    { id: "marks", label: "Mark sheet", icon: ClipboardEdit },
    { id: "candidates", label: "Candidates and seats", icon: Users },
    ...(isManager ? [{ id: "periods" as const, label: "Exam periods", icon: CalendarRange }] : []),
    { id: "types", label: "Kinds of exam", icon: Tags },
  ];
  const [params, setParams] = useSearchParams();
  const tab: Tab = tabs.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "papers";
  const examId = params.get("exam") ? Number(params.get("exam")) : null;
  const select = (next: Tab, exam?: number | null) => {
    const query: Record<string, string> = {};
    if (next !== "papers") query.tab = next;
    if (exam) query.exam = String(exam);
    setParams(query, { replace: true });
  };

  const queryClient = useQueryClient();
  const exams = useQuery({ queryKey: ["exams", "papers"], queryFn: examinationService.getExams });
  const types = useQuery({ queryKey: ["exams", "types"], queryFn: examinationService.getExamTypes });
  const periods = useQuery({ queryKey: ["exams", "periods"], queryFn: examinationService.getExamSchedules, enabled: isManager });
  const subjects = useQuery({ queryKey: ["exams", "subjects"], queryFn: () => getAll<SubjectOption>("/examination/subjects/"), staleTime: 10 * 60_000 });
  const years = useQuery({ queryKey: ["exams", "years"], queryFn: academicSetupService.getYears, staleTime: 10 * 60_000 });
  const terms = useQuery({ queryKey: ["exams", "terms"], queryFn: academicSetupService.getTerms, staleTime: 10 * 60_000 });
  const classes = useQuery({ queryKey: ["exams", "classes"], queryFn: () => getAll<ClassOption>("/students/classes/"), staleTime: 10 * 60_000 });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["exams"] });

  const rows = exams.data ?? [];
  const mine = rows.filter((row) => nextAction(row, can) !== null);
  const marking = rows.filter((row) => row.results_status === "draft" && row.candidate_count > 0);
  const review = rows.filter((row) => row.results_status === "submitted" || row.results_status === "moderated").length;
  const ready = rows.filter((row) => row.results_status === "approved").length;
  const published = rows.filter((row) => row.results_status === "published").length;
  const dash = (text: string) => (exams.isLoading ? "–" : text);

  const tiles: { label: string; value: string; note: string; go: () => void; icon: LucideIcon; alert?: boolean }[] = [
    { label: "Waiting for you", value: dash(String(mine.length)), note: mine.length ? "Papers to mark, submit or decide" : "Nothing waiting", go: () => select("papers"), icon: Inbox, alert: mine.length > 0 },
    { label: "Being marked", value: dash(String(marking.length)), note: `${marking.reduce((sum, row) => sum + row.marked_count, 0)} of ${marking.reduce((sum, row) => sum + row.candidate_count, 0)} candidates marked`, go: () => select("papers"), icon: ClipboardEdit },
    { label: "In review", value: dash(String(review)), note: ready ? `${ready} ready to publish` : "Moderation and approval", go: () => select("papers"), icon: ShieldCheck, alert: ready > 0 && can.canPublish },
    { label: "Published", value: dash(String(published)), note: "Results learners and parents can see", go: () => select("papers"), icon: Send },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Examinations</h1>
        <p className="mt-1 text-sm text-gray-500">Set papers, mark them, and take results through moderation and approval to publication. Teacher submits, the head of department moderates, the head teacher approves, the director of studies publishes.</p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((tile) => (
          <button
            key={tile.label}
            type="button"
            onClick={tile.go}
            className="flex items-center gap-4 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-primary-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
          >
            <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${tile.alert ? "bg-amber-50 text-amber-700" : "bg-primary-50 text-primary-700"}`}>
              <tile.icon className="h-5 w-5" />
            </span>
            <span className="min-w-0">
              <span className="block text-xs font-medium text-gray-500">{tile.label}</span>
              <span className={`block truncate text-xl font-semibold ${tile.alert ? "text-amber-800" : "text-gray-900"}`}>{tile.value}</span>
              <span className="block truncate text-xs text-gray-500">{tile.note}</span>
            </span>
          </button>
        ))}
      </div>

      <nav role="tablist" aria-label="Examination sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {tabs.map((item) => {
          const active = tab === item.id;
          const badge = item.id === "papers" ? mine.length : 0;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => select(item.id, item.id === "marks" ? examId : null)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"}`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {badge ? <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-xs tabular-nums text-amber-800">{badge}</span> : null}
            </button>
          );
        })}
      </nav>

      {tab === "papers" && (
        <PapersPanel
          exams={exams.data}
          types={types.data ?? []}
          subjects={subjects.data ?? []}
          years={years.data ?? []}
          terms={terms.data ?? []}
          classes={classes.data ?? []}
          me={me}
          isManager={isManager}
          can={can}
          isLoading={exams.isLoading}
          isError={exams.isError}
          onRetry={() => exams.refetch()}
          onChanged={refresh}
          onMark={(id) => select("marks", id)}
        />
      )}
      {tab === "marks" && <MarkSheetPanel exams={rows} examId={examId} onPick={(id) => select("marks", id)} onChanged={refresh} />}
      {tab === "candidates" && <CandidatesPanel enabled />}
      {tab === "periods" && isManager && (
        <PeriodsPanel periods={periods.data} exams={rows} years={years.data ?? []} terms={terms.data ?? []} classes={classes.data ?? []} isLoading={periods.isLoading} isError={periods.isError} onRetry={() => periods.refetch()} onChanged={refresh} />
      )}
      {tab === "types" && <TypesPanel types={types.data} canEdit={isManager} isLoading={types.isLoading} isError={types.isError} onRetry={() => types.refetch()} onChanged={refresh} />}
    </div>
  );
}
